import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import type { ApprovalStageView } from '../../types/pettyCash';
import { formatDateTime } from '../../utils/pettyCash';

/** The claim's approval path: the PR flow's stages, then the finance review. */
const ApprovalPath: React.FC<{ stages: ApprovalStageView[]; posted?: boolean }> = ({ stages, posted }) => {
  if (!stages.length) return <Typography variant="body2" color="text.secondary">The approval path is set when the claim is submitted.</Typography>;
  return (
    <Stack component="ol" direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 3 }} sx={{ m: 0, p: 0, listStyle: 'none' }} flexWrap="wrap" useFlexGap>
      {stages.map((s) => (
        <Stack component="li" key={s.step} direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              flex: '0 0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: '1px',
              ...(s.status === 'Done'
                ? { bgcolor: '#1e6b2a', color: '#fff' }
                : s.status === 'Current'
                  ? { border: '2px solid #E8B640', bgcolor: '#fdf3cf' }
                  : { border: '1px solid #9aa6ad' }),
            }}
          >
            {s.status === 'Done' ? <CheckIcon sx={{ fontSize: 14 }} /> : null}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={s.status === 'Current' ? 600 : 400}>
              {s.name}{s.kind === 'Finance' ? '' : ` (stage ${s.step})`}
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                {s.status === 'Done' ? ': approved' : s.status === 'Current' ? ': waiting' : ''}
              </Box>
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {/* A later stage's approvers are found only when the claim reaches it. */}
              {s.approvers.length
                ? s.approvers.map((a) => `${a.name}${a.status === 'Approved' ? ` ✓${a.decidedAt ? ` ${formatDateTime(a.decidedAt)}` : ''}` : a.status === 'Skipped' ? ' (not needed)' : ''}`).join(', ')
                : s.status === 'Waiting' ? 'Not reached yet' : 'No approver found'}
              {s.approvers.length > 1 ? (s.rule === 'ALL' ? ' · all must approve' : ' · any one approves') : ''}
            </Typography>
          </Box>
        </Stack>
      ))}
      <Stack component="li" direction="row" spacing={1} alignItems="center">
        <Box aria-hidden="true" sx={{ width: 22, height: 22, borderRadius: '50%', ...(posted ? { bgcolor: '#1e6b2a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' } : { border: '1px solid #9aa6ad' }) }}>
          {posted ? <CheckIcon sx={{ fontSize: 14 }} /> : null}
        </Box>
        <Typography variant="body2" color={posted ? 'text.primary' : 'text.secondary'}>Posted to SAP</Typography>
      </Stack>
    </Stack>
  );
};

export default ApprovalPath;
